import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Snackbar,
  Typography,
} from '@mui/material';
import { TableTitleBox } from '../../common/components';
import { useUploadActiveAgreement } from '../../hooks/useDebt';
import { useDebtStatusTable } from './hooks/useDebtStatusTable';
import DebtStatusGrid from './components/DebtStatusTable';
import DebtStatusFilters from './components/DebtStatusFilters';
import AgreementViewerDialog from './components/AgreementViewerDialog';

const DebtStatus = () => {
  const {
    loading,
    error,
    rows,
    header,
    filters,
    pagination,
    paymentMethods,
    paymentMethodsLoading,
    handleFilterChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleApplyFilters,
    reload,
  } = useDebtStatusTable();
  const uploadAgreementMutation = useUploadActiveAgreement();
  const fileInputRef = useRef(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [viewer, setViewer] = useState({ open: false, url: '', title: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleUploadClick = (row) => {
    setSelectedRow(row);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selectedRow?.contract_uuid) {
      return;
    }

    try {
      await uploadAgreementMutation.mutateAsync({
        contractUuid: selectedRow.contract_uuid,
        file,
      });
      setSnackbar({
        open: true,
        message: 'Acuerdo vigente actualizado correctamente.',
        severity: 'success',
      });
      await reload();
    } catch (uploadError) {
      const apiMessage = uploadError?.response?.data?.message;
      setSnackbar({
        open: true,
        message: apiMessage || 'No se pudo actualizar el archivo de acuerdo.',
        severity: 'error',
      });
    } finally {
      setSelectedRow(null);
    }
  };

  const handleViewAgreement = (row) => {
    const agreement = row?.active_agreement;
    if (!agreement?.file_url) {
      return;
    }
    setViewer({
      open: true,
      url: agreement.file_url,
      title: `Contrato ${row.contract_number}`,
    });
  };

  return (
    <Box>
      <TableTitleBox title="Habitat 99 | Mora | Estado de Mora" />

      <DebtStatusFilters
        filters={filters}
        paymentMethods={paymentMethods}
        paymentMethodsLoading={paymentMethodsLoading}
        loading={loading}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
      />

      {error && (
        <Typography sx={{ mt: 2 }} color="error">
          {error}
        </Typography>
      )}

      <DebtStatusGrid
        rows={rows}
        loading={loading}
        totalContracts={header?.total_contracts ?? rows.length}
        onReload={reload}
        pagination={pagination}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        showAgreementActions
        onUploadAgreement={handleUploadClick}
        onViewAgreement={handleViewAgreement}
        uploadingAgreementContractUuid={
          uploadAgreementMutation.isPending
            ? uploadAgreementMutation.variables?.contractUuid
            : null
        }
      />

      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />

      <AgreementViewerDialog
        open={viewer.open}
        title={viewer.title}
        fileUrl={viewer.url}
        onClose={() => setViewer({ open: false, url: '', title: '' })}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DebtStatus;
